'use strict'
import * as React from 'react'
import { useCallback } from 'react'
import {
  trimBothSpaces,
  normalizeErrorsForShow,
  isPlaceholderDashShortName,
  validateOrgImportRowAfterEdit,
  getSegmentNameSetFromList
} from '../extra/helpers'
import { validateDepartment } from '../extra/validation'
import { DepartmentOrgFormModalPropsSchema } from '../extra/types'
import { ChrofyShowErrors } from '../../../common/Form'
import { ChrofyCustomSelect } from '../../../common/ChrofyCustomSelect'

/** 一覧では欠損を「-」等で表示するが、編集モーダルでは空欄＋プレースホルダーに合わせる */
function shortNameInitialForInput(departmentShortName) {
  if (isPlaceholderDashShortName(departmentShortName)) return ''
  return trimBothSpaces((departmentShortName ?? '').toString())
}

/**
 * 組織情報登録・編集モーダル用ボディ（設計書 4.2, 4.3）
 *
 * @param {Object} props
 * @param {boolean} props.isEdit - True: 編集モーダル
 * @param {{
 *   department_code?: string
 *   department_name?: string
 *   department_short_name?: string
 *   segment_name?: string
 * }} [props.initial]
 *   - 初期値（編集時）
 *
 * @param {({ segment_name?: string; segment_code?: string } | string)[]} props.segmentList - セグメント一覧
 * @param {Set<string>} [props.existingCodes] - 重複チェック用（追加時）
 * @param {() => void} [props.onClose] - キャンセル時
 * @param {(data: {
 *   department_code: string
 *   department_name: string
 *   department_short_name: string
 *   segment_name: string
 * }) => void} props.onSubmit
 *   - 送信時
 *
 * @param {(errors: { [key: string]: string }) => void} [props.onError] - 項目別エラー
 * @returns {JSX.Element}
 */
export function DepartmentOrgFormModal({
  isEdit = false,
  initial = {},
  segmentList = [],
  existingCodes = new Set(),
  onClose,
  onSubmit,
  onError
}) {
  // Propsの型チェック（開発環境のみ）
  if (process.env.NODE_ENV === 'development') {
    const propsValidation = DepartmentOrgFormModalPropsSchema.safeParse({
      isEdit,
      initial,
      segmentList,
      existingCodes,
      onClose,
      onSubmit,
      onError
    })
    if (!propsValidation.success) {
      console.warn('[DepartmentOrgFormModal] Invalid props:', propsValidation.error)
    }
    // onSubmitは必須
    if (!onSubmit || typeof onSubmit !== 'function') {
      console.error('[DepartmentOrgFormModal] onSubmit prop is required and must be a function')
    }
    // existingCodesはSet型であることを確認
    if (existingCodes && !(existingCodes instanceof Set)) {
      console.warn('[DepartmentOrgFormModal] existingCodes should be a Set instance')
    }
  }
  const [code, setCode] = React.useState(initial.department_code ?? '')
  const [name, setName] = React.useState(initial.department_name ?? '')
  const [shortName, setShortName] = React.useState(shortNameInitialForInput(initial.department_short_name))
  const [segmentName, setSegmentName] = React.useState(() => {
    const raw = trimBothSpaces(initial.segment_name ?? '')
    const segSet = getSegmentNameSetFromList(segmentList)
    if (isEdit && segmentList.length > 0 && raw && !segSet.has(raw)) return ''
    return initial.segment_name ?? ''
  })
  const [errors, setErrors] = React.useState({})

  // Modal width を資格情報設定と同じく 750px に設定
  // ChrofyCustomSelect のドロップダウンは body へポータル描画（position: fixed）されるため、
  // モーダル側は通常どおり overflow-y-auto でスクロールさせてもドロップダウンはクリップされない。
  // ビューポートが低い環境（DevTools 表示中など）でも内容がモーダル外へはみ出さないようにする（#3232）
  React.useLayoutEffect(() => {
    const modalWindowElement = document.querySelector('.modal-window')
    const modalWindowClassList = ['w-[initial]', 'max-h-[97vh]', 'max-w-[750px]', 'overflow-y-auto']

    modalWindowElement?.classList.add(...modalWindowClassList)
    return () => {
      modalWindowElement?.classList.remove(...modalWindowClassList)
    }
  }, [])

  React.useEffect(() => {
    setCode(initial.department_code ?? '')
    setName(initial.department_name ?? '')
    setShortName(shortNameInitialForInput(initial.department_short_name))
    const rawSeg = trimBothSpaces(initial.segment_name ?? '')
    const segSet = getSegmentNameSetFromList(segmentList)
    setSegmentName(
      isEdit && segmentList.length > 0 && rawSeg && !segSet.has(rawSeg) ? '' : (initial.segment_name ?? '')
    )
    setErrors({})
  }, [
    isEdit,
    initial.department_code,
    initial.department_name,
    initial.department_short_name,
    initial.segment_name,
    segmentList
  ])

  const handleSubmit = () => {
    const trimmed = {
      department_code: trimBothSpaces(code),
      department_name: trimBothSpaces(name),
      department_short_name: trimBothSpaces(shortName),
      segment_name: trimBothSpaces(segmentName)
    }
    const errs = validateDepartment(trimmed, existingCodes, isEdit ? (initial.department_code ?? '') : null)
    if (Object.keys(errs).length > 0) {
      setErrors(errs)
      onError?.(errs)
      return
    }
    setErrors({})
    onSubmit(trimmed)
  }

  const segmentOptions = Array.isArray(segmentList) ? segmentList : []
  const getSegmentLabel = (s) => (typeof s === 'string' ? s : (s?.segment_name ?? s?.segment_code ?? ''))
  const segmentSelectOptions = React.useMemo(
    () =>
      segmentOptions.map((s) => {
        const label = getSegmentLabel(s)
        const val = typeof s === 'string' ? s : (s?.segment_name ?? s?.segment_code ?? '')
        return { label, value: val }
      }),
    [segmentOptions]
  )
  const rawInitialSegment = trimBothSpaces(initial.segment_name ?? '')
  const segmentNameSet = React.useMemo(() => getSegmentNameSetFromList(segmentList), [segmentList])
  const invalidImportedSegmentName =
    isEdit && segmentList.length > 0 && rawInitialSegment && !segmentNameSet.has(rawInitialSegment)
      ? rawInitialSegment
      : ''

  return (
    <>
      <div className="text-base">
        <table className="form-table table-fixed w-full">
          <tbody>
            {/* 組織コード */}
            <tr>
              <th className="form-table__col-head w-[150px]">
                <label htmlFor="department_code" className="text-h7 font-bold">
                  組織コード <span className="red--text">*</span>
                </label>
              </th>
              <td className="form-table__col-data">
                <input
                  id="department_code"
                  type="text"
                  className={`text-input w-full rounded-lg px-4 py-2.5 text-h7 text-left min-h-[46px] ${
                    errors.department_code ? '!border-red-500' : '!border-gray-300'
                  }`}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="例：D0001"
                />
                <ChrofyShowErrors field="department_code" errors={normalizeErrorsForShow(errors)} />
              </td>
            </tr>
            {/* 組織名 */}
            <tr>
              <th className="form-table__col-head w-[150px]">
                <label htmlFor="department_name" className="text-h7 font-bold">
                  組織名 <span className="red--text">*</span>
                </label>
              </th>
              <td className="form-table__col-data">
                <input
                  id="department_name"
                  type="text"
                  className={`text-input w-full rounded-lg px-4 py-2.5 text-h7 text-left min-h-[46px] ${
                    errors.department_name ? '!border-red-500' : '!border-gray-300'
                  }`}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="例：営業部"
                />
                <ChrofyShowErrors field="department_name" errors={normalizeErrorsForShow(errors)} />
              </td>
            </tr>
            {/* 組織略称 */}
            <tr>
              <th className="form-table__col-head w-[150px]">
                <label htmlFor="department_short_name" className="text-h7 font-bold">
                  組織略称
                </label>
              </th>
              <td className="form-table__col-data">
                <input
                  id="department_short_name"
                  type="text"
                  className={`text-input w-full rounded-lg px-4 py-2.5 text-h7 text-left min-h-[46px] ${
                    errors.department_short_name ? '!border-red-500' : '!border-gray-300'
                  }`}
                  value={shortName}
                  onChange={(e) => setShortName(e.target.value)}
                  placeholder="例：営業"
                />
                <ChrofyShowErrors field="department_short_name" errors={normalizeErrorsForShow(errors)} />
              </td>
            </tr>
            {/* セグメント名 */}
            <tr>
              <th className="form-table__col-head w-[150px]">
                <label htmlFor="segment_name" className="text-h7 font-bold">
                  セグメント名
                </label>
              </th>
              <td className="form-table__col-data">
                {/* 年収計算設定「■出向者の年収計算への集計設定」と同じ ChrofyCustomSelect（先頭は 未選択） */}
                {/* 100%ズーム時もタスクバー・ブラウザ下端に被らないよう、利用可能高さをやや小さめに見積もる（90%ズーム時と同様の余白感） */}
                <div className="relative z-20">
                  <ChrofyCustomSelect
                    id="segment_name"
                    name="segment_name"
                    className="w-full max-w-[350px]"
                    value={segmentName}
                    onChange={(e) => setSegmentName(e.target.value ?? '')}
                    options={segmentSelectOptions}
                    isError={!!errors.segment_name}
                    hasNoSelect
                    lightNoSelect
                    data-testid="org_form_segment_select"
                    dropdownClassName="!max-h-[min(30vh,14rem,calc(100dvh*0.9-6rem))] overflow-y-auto z-[100]"
                  />
                </div>
                {invalidImportedSegmentName ? (
                  <p className="text-red-500 text-sm" data-testid="invalid_import_segment_notice">
                    「{invalidImportedSegmentName}」はマスタ未登録です。一覧から選び直してください。
                  </p>
                ) : null}
                <ChrofyShowErrors field="segment_name" errors={normalizeErrorsForShow(errors)} />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="modal-content-footer mt-3">
        <div className="footer_button_area gap-x-16">
          <button
            type="button"
            className="footer-btn button_back"
            data-testid="modal_cancel"
            onClick={() => onClose?.()}
          >
            キャンセル
          </button>
          <button type="button" className="footer-btn button_base" onClick={handleSubmit} data-testid="org_form_submit">
            {isEdit ? '更新' : '追加'}
          </button>
        </div>
      </div>
    </>
  )
}

/**
 * 組織情報登録・編集モーダルを開くフック
 *
 * @param {Object} opts
 * @param {Function} opts.showModal - UseModal()
 * @param {{ _closeUpModal: () => void }} opts.modalExtra - UseModalExtra()
 * @param {Function} opts.dispatch - ストア dispatch
 * @param {Array} opts.segmentList - セグメント一覧
 * @param {Array} opts.currentDepartments - 現在の組織一覧
 * @param {() => void} opts.updateHierarchyValidation - 階層バリデーション更新
 * @param {string} [opts.corporationNumber] - 法人番号（新規追加時ガード用）
 * @param {string} [opts.currentPeriodKey] - 対象期間キー（新規追加時ガード用）
 * @returns {{ openAddOrgModal: () => void; openEditOrgModal: (row: Object) => void }}
 */
export function useDepartmentOrgFormModal({
  showModal,
  modalExtra,
  dispatch,
  segmentList,
  currentDepartments,
  updateHierarchyValidation,
  corporationNumber = '',
  currentPeriodKey = ''
}) {
  const openAddOrgModal = useCallback(() => {
    if (!corporationNumber || !currentPeriodKey) return
    const existingCodes = new Set(currentDepartments.map((d) => d.department_code).filter(Boolean))
    showModal({
      title: '組織情報登録',
      description: '組織情報を入力してください。',
      isWide: false,
      isCancel: false,
      body: (
        <DepartmentOrgFormModal
          key={Date.now()}
          isEdit={false}
          segmentList={segmentList}
          existingCodes={existingCodes}
          onClose={() => modalExtra._closeUpModal()}
          onSubmit={(data) => {
            dispatch('ADD_DEPARTMENT', {
              department_code: data.department_code,
              department_name: data.department_name,
              department_short_name: data.department_short_name,
              segment_name: data.segment_name,
              status: 'new'
            })
            modalExtra._closeUpModal()
            updateHierarchyValidation()
          }}
          onError={() => {}}
        />
      )
    })
  }, [
    showModal,
    modalExtra,
    dispatch,
    segmentList,
    currentDepartments,
    updateHierarchyValidation,
    corporationNumber,
    currentPeriodKey
  ])

  const openEditOrgModal = useCallback(
    (row) => {
      if ((row?.status || '') === 'deleted') return
      const existingCodes = new Set(currentDepartments.map((d) => d.department_code).filter(Boolean))
      showModal({
        title: '組織情報編集',
        isWide: false,
        isCancel: false,
        body: (
          <DepartmentOrgFormModal
            key={`${row.department_code}_${Date.now()}`}
            isEdit
            initial={row}
            segmentList={segmentList}
            existingCodes={existingCodes}
            onClose={() => modalExtra._closeUpModal()}
            onSubmit={(data) => {
              dispatch('UPDATE_DEPARTMENT', {
                department_code: row.department_code,
                updates: {
                  department_code: data.department_code,
                  department_name: data.department_name,
                  department_short_name: data.department_short_name,
                  segment_name: data.segment_name
                }
              })
              // モーダル body は ModalProvider 配下で DepartmentProvider の外に描画されるため、
              // useStore() は組織ストアではなくルートストアになる。インポート行エラー再計算は currentDepartments をマージして行う。
              const rawIdx = row._importRowIndex
              const importIdx =
                typeof rawIdx === 'number' && Number.isFinite(rawIdx)
                  ? rawIdx
                  : typeof rawIdx === 'string' && rawIdx !== ''
                    ? Number.parseInt(rawIdx, 10)
                    : NaN
              if (Number.isFinite(importIdx) && importIdx >= 0) {
                const merged = currentDepartments.map((d) =>
                  (d.department_code || '') === (row.department_code || '')
                    ? {
                        ...d,
                        department_code: data.department_code,
                        department_name: data.department_name,
                        department_short_name: data.department_short_name,
                        segment_name: data.segment_name
                      }
                    : d
                )
                const existingCodesAfterEdit = new Set(merged.map((d) => d.department_code).filter(Boolean))
                const rowErrs = validateOrgImportRowAfterEdit(
                  data,
                  segmentList,
                  existingCodesAfterEdit,
                  data.department_code
                )
                dispatch('PATCH_ORG_IMPORT_ROW_ERRORS', { importRowIndex: importIdx, errors: rowErrs })
              }
              modalExtra._closeUpModal()
              updateHierarchyValidation()
            }}
            onError={() => {}}
          />
        )
      })
    },
    [showModal, modalExtra, dispatch, segmentList, currentDepartments, updateHierarchyValidation]
  )

  return { openAddOrgModal, openEditOrgModal }
}
